import { fichaDoHtml, htmlDaDescricao } from './kmzFicha';

const LIMITE_FEATURES = 40000;
export const PREFIXO_ICONE_ZIP = 'zip://';

const MIME_IMAGEM = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

function lerCoordenadas(texto) {
  const pares = [];
  let altitude = 0;
  String(texto || '').trim().split(/\s+/).forEach((token) => {
    const partes = token.split(',');
    const lng = Number(partes[0]);
    const lat = Number(partes[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    pares.push([lng, lat]);
    const alt = Number(partes[2]);
    if (Number.isFinite(alt) && alt > altitude) altitude = alt;
  });
  return { pares, altitude };
}

function filho(el, nome) {
  if (!el || !el.children) return null;
  const kids = el.children;
  for (let i = 0; i < kids.length; i += 1) {
    if (kids[i].localName === nome) return kids[i];
  }
  return null;
}

function texto(el, nome) {
  const no = filho(el, nome);
  return no && no.textContent ? no.textContent.trim() : '';
}

function nos(doc, nome) {
  if (doc.getElementsByTagNameNS) {
    const lista = doc.getElementsByTagNameNS('*', nome);
    if (lista && lista.length) return Array.from(lista);
  }
  return Array.from(doc.getElementsByTagName(nome));
}

export function corDoKml(valor) {
  const limpo = String(valor || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]+$/.test(limpo)) return '';
  if (limpo.length === 8) {
    return `#${limpo.slice(6, 8)}${limpo.slice(4, 6)}${limpo.slice(2, 4)}`.toLowerCase();
  }
  if (limpo.length === 6) return `#${limpo}`.toLowerCase();
  return '';
}

function idDoEstilo(url) {
  const textoUrl = String(url || '').trim();
  const hash = textoUrl.lastIndexOf('#');
  return hash >= 0 ? textoUrl.slice(hash + 1) : textoUrl;
}

function indiceDeEstilos(doc) {
  const estilos = new Map();
  const mapas = new Map();
  nos(doc, 'Style').forEach((style) => {
    const id = style.getAttribute('id');
    if (id) estilos.set(id, style);
  });
  nos(doc, 'StyleMap').forEach((mapa) => {
    const id = mapa.getAttribute('id');
    const pares = nos(mapa, 'Pair');
    const normal = pares.find((par) => texto(par, 'key') === 'normal') || pares[0];
    const url = normal && texto(normal, 'styleUrl');
    if (id && url) mapas.set(id, url);
  });
  return { estilos, mapas };
}

function estiloPorUrl(url, indice, visto) {
  const id = idDoEstilo(url);
  if (!id || visto.has(id)) return null;
  visto.add(id);
  if (indice.mapas.has(id)) return estiloPorUrl(indice.mapas.get(id), indice, visto);
  return indice.estilos.get(id) || null;
}

function estiloDoElemento(el, indice) {
  let atual = el;
  while (atual) {
    const inline = filho(atual, 'Style');
    // Style com id é estilo compartilhado: só vale por styleUrl. No Document ou na pasta, só o
    // Style sem id se aplica aos filhos.
    if (inline && (atual === el || !inline.getAttribute('id'))) return inline;
    const url = texto(atual, 'styleUrl');
    if (url) {
      const resolvido = estiloPorUrl(url, indice, new Set());
      if (resolvido) return resolvido;
    }
    if (atual.localName === 'Document' || atual.localName === 'kml') break;
    atual = atual.parentElement;
  }
  return null;
}

function corDoEstilo(style, ehPonto) {
  if (!style) return '';
  const linha = filho(style, 'LineStyle');
  const icone = filho(style, 'IconStyle');
  const poly = filho(style, 'PolyStyle');
  const bruto = ehPonto
    ? texto(icone, 'color') || texto(linha, 'color') || texto(poly, 'color')
    : texto(linha, 'color') || texto(poly, 'color') || texto(icone, 'color');
  return corDoKml(bruto);
}

// Cor que o KML multiplica sobre o PNG do ícone. Sem cor, ou branca, o PNG sai como veio.
function corDoIcone(style) {
  const icone = style && filho(style, 'IconStyle');
  const cor = corDoKml(texto(icone, 'color'));
  return cor === '#ffffff' ? '' : cor;
}

// null quando o estilo não diz nada: o Earth trata ausência como escala 1.
function escalaDoEstilo(style, bloco) {
  const no = style && filho(style, bloco);
  const bruto = texto(no, 'scale');
  if (!bruto) return null;
  const valor = Number(bruto);
  return Number.isFinite(valor) && valor >= 0 ? valor : null;
}

function hrefRelativo(href) {
  return Boolean(href) && !/^[a-z][a-z0-9+.-]*:/i.test(href) && !href.startsWith('//');
}

function caminhoDoHref(href) {
  return String(href).trim().replace(/\\/g, '/').replace(/^\.?\//, '');
}

function arquivoDoIcone(chave) {
  const partes = String(chave || '').split('/');
  return (partes[partes.length - 1] || '').toLowerCase();
}

// Menor vence a colisão: subestação, depois trafo, depois o resto.
function ordemDoIcone(chave) {
  const arquivo = arquivoDoIcone(chave);
  if (arquivo.includes('square') || arquivo.includes('subestacao')) return 0;
  if (arquivo.includes('triangle') || arquivo.includes('trafo')) return 1;
  return 2;
}

// Serviço da faixa: o pinheiro do Earth (parks.png) ou o estilo ServicoFaixa-*.
function ehArvore(href, estiloId) {
  if (arquivoDoIcone(href) === 'parks.png') return true;
  return /^servicofaixa/i.test(String(estiloId || ''));
}

function larguraDoEstilo(style) {
  const linha = style && filho(style, 'LineStyle');
  const valor = Number(texto(linha, 'width'));
  if (!Number.isFinite(valor) || valor <= 0) return 4;
  return Math.max(valor, 4);
}

function hrefDoEstilo(style) {
  const icone = style && filho(style, 'IconStyle');
  const icon = icone && filho(icone, 'Icon');
  return texto(icon, 'href');
}

// Caminho de pastas e visibilidade herdada, calculados uma vez por pasta: uma pasta com
// milhares de placemarks não pode ser varrida de novo a cada um deles.
function infoDaPasta(pasta, cache) {
  if (!pasta || pasta.localName === 'kml') return { caminho: '', oculta: false };
  if (cache.has(pasta)) return cache.get(pasta);
  const pai = infoDaPasta(pasta.parentElement, cache);
  let info = pai;
  if (pasta.localName === 'Folder') {
    const nome = texto(pasta, 'name') || 'Pasta';
    info = {
      caminho: pai.caminho ? `${pai.caminho} / ${nome}` : nome,
      oculta: pai.oculta || texto(pasta, 'visibility') === '0',
    };
  } else if (pasta.localName === 'Document') {
    info = { caminho: pai.caminho, oculta: pai.oculta || texto(pasta, 'visibility') === '0' };
  }
  cache.set(pasta, info);
  return info;
}

function geometriasDoPlacemark(pm) {
  const saida = [];
  const visitar = (el) => {
    if (!el || !el.children) return;
    const kids = el.children;
    for (let i = 0; i < kids.length; i += 1) {
      const no = kids[i];
      if (no.localName === 'Point' || no.localName === 'LineString' || no.localName === 'LinearRing') {
        const coords = filho(no, 'coordinates');
        if (coords) {
          saida.push({
            tipo: no.localName === 'Point' ? 'Point' : 'LineString',
            texto: coords.textContent,
          });
        }
      } else if (no.localName === 'Polygon' || no.localName === 'MultiGeometry') {
        visitar(no);
      }
    }
  };
  visitar(pm);
  return saida;
}

// Desenha o arquivo como o Google Earth: nada some por ter estilo desconhecido. Escala 0 em
// IconStyle/LabelStyle só liga as marcas semSimbolo/semRotulo, e visibility 0 (no placemark ou em
// qualquer pasta acima) só liga oculto/pastaOculta. Quem desenha decide o que fazer com elas.
// href relativo vira uma chave `zip://<escopo>/<caminho>`; geometriaDoArquivo troca a chave
// pelo PNG de dentro do KMZ.
export function geometriaDoKml(xmlText, opcoes = {}) {
  const escopo = (opcoes && opcoes.escopo) || '';
  const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error('KML inválido');
  }
  const indice = indiceDeEstilos(doc);
  const pastas = new Map();
  const iconesRelativos = {};
  const features = [];
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let truncado = false;

  const chaveDoIcone = (href) => {
    if (!hrefRelativo(href)) return href;
    const caminho = caminhoDoHref(href);
    const chave = `${PREFIXO_ICONE_ZIP}${escopo}/${caminho}`;
    iconesRelativos[chave] = caminho;
    return chave;
  };

  nos(doc, 'Placemark').forEach((pm) => {
    if (truncado) return;
    const estilo = estiloDoElemento(pm, indice);
    const nome = texto(pm, 'name');
    const html = htmlDaDescricao(filho(pm, 'description'));
    const pasta = infoDaPasta(pm.parentElement, pastas);
    const estiloId = idDoEstilo(texto(pm, 'styleUrl'));
    const oculto = texto(pm, 'visibility') === '0';
    const escalaIcone = escalaDoEstilo(estilo, 'IconStyle');
    const escalaRotulo = escalaDoEstilo(estilo, 'LabelStyle');
    geometriasDoPlacemark(pm).forEach((geo) => {
      if (features.length >= LIMITE_FEATURES) {
        truncado = true;
        return;
      }
      const { pares, altitude } = lerCoordenadas(geo.texto);
      if (!pares.length) return;
      const ehPonto = geo.tipo === 'Point' || pares.length === 1;
      const coordinates = ehPonto ? pares[0] : pares;
      const href = ehPonto ? hrefDoEstilo(estilo) : '';
      const arvore = ehPonto && ehArvore(href, estiloId);
      const hrefIcone = href || (arvore ? 'https://maps.google.com/mapfiles/kml/shapes/parks.png' : '');
      const iconeUrl = hrefIcone ? chaveDoIcone(hrefIcone) : '';
      const corResolvida = corDoEstilo(estilo, ehPonto) || '#808080';
      (ehPonto ? [coordinates] : coordinates).forEach(([lng, lat]) => {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      });
      const ficha = fichaDoHtml(nome, html, { linha: !ehPonto });
      features.push({
        type: 'Feature',
        properties: {
          nome,
          pasta: pasta.caminho,
          estiloId,
          kmlStyle: ficha.kmlStyle || '',
          ficha: JSON.stringify(ficha),
          ...(ficha.nsmpId ? { nsmpId: ficha.nsmpId } : {}),
          corResolvida,
          largura: ehPonto ? 4 : larguraDoEstilo(estilo),
          ...(iconeUrl ? {
            iconeUrl,
            corIcone: iconeUrl.startsWith(PREFIXO_ICONE_ZIP) ? corDoIcone(estilo) : corResolvida,
            ordemIcone: ordemDoIcone(iconeUrl),
          } : {}),
          ...(ehPonto && escalaIcone !== null && escalaIcone !== 1 ? { iconeEscala: escalaIcone } : {}),
          ...(ehPonto && escalaIcone === 0 ? { semSimbolo: true } : {}),
          ...(arvore ? { arvore: true } : {}),
          ...(escalaRotulo === 0 ? { semRotulo: true } : {}),
          ...(oculto ? { oculto: true } : {}),
          ...(pasta.oculta ? { pastaOculta: true } : {}),
          ...(!ehPonto && altitude ? { ordemLinha: altitude } : {}),
        },
        geometry: { type: ehPonto ? 'Point' : 'LineString', coordinates },
      });
    });
  });

  if (!features.length) throw new Error('Este KMZ não tem geometria para desenhar.');
  return {
    collection: { type: 'FeatureCollection', features },
    bounds: [[minLng, minLat], [maxLng, maxLat]],
    pontoUnico: minLng === maxLng && minLat === maxLat,
    truncado,
    iconesRelativos,
  };
}

async function inflar(bytes) {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Não foi possível ler a compactação deste KMZ.');
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

function proximaAssinatura(bytes, from) {
  for (let i = from; i + 4 <= bytes.length; i += 1) {
    if (bytes[i] !== 0x50 || bytes[i + 1] !== 0x4b) continue;
    const marca = bytes[i + 2] === 0x03 && bytes[i + 3] === 0x04;
    const central = bytes[i + 2] === 0x01 && bytes[i + 3] === 0x02;
    if (marca || central) return i;
  }
  return bytes.length;
}

function entradasDoZip(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let fim = -1;
  const limite = Math.max(0, bytes.length - (22 + 0xffff));
  for (let i = bytes.length - 22; i >= limite; i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      fim = i;
      break;
    }
  }
  if (fim < 0) return [];
  const total = view.getUint16(fim + 10, true);
  let cursor = view.getUint32(fim + 16, true);
  const entradas = [];
  for (let n = 0; n < total && cursor + 46 <= bytes.length; n += 1) {
    if (view.getUint32(cursor, true) !== 0x02014b50) break;
    const method = view.getUint16(cursor + 10, true);
    const compSize = view.getUint32(cursor + 20, true);
    const nameLen = view.getUint16(cursor + 28, true);
    const extraLen = view.getUint16(cursor + 30, true);
    const commentLen = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(cursor + 46, cursor + 46 + nameLen));
    entradas.push({ method, compSize, name, localOffset });
    cursor += 46 + nameLen + extraLen + commentLen;
  }
  return entradas;
}

function fatiaLocal(bytes, view, localOffset, compSize) {
  if (localOffset + 30 > bytes.length || view.getUint32(localOffset, true) !== 0x04034b50) {
    throw new Error('Não encontrei o KML dentro do KMZ.');
  }
  const nameLen = view.getUint16(localOffset + 26, true);
  const extraLen = view.getUint16(localOffset + 28, true);
  const dataStart = localOffset + 30 + nameLen + extraLen;
  const dataEnd = dataStart + compSize;
  if (dataEnd > bytes.length) throw new Error('Não encontrei o KML dentro do KMZ.');
  return bytes.subarray(dataStart, dataEnd);
}

async function textoDoKml(method, cru) {
  if (method === 0) return new TextDecoder().decode(cru);
  if (method !== 8) throw new Error('Compactação do KMZ não suportada.');
  try {
    return new TextDecoder().decode(await inflar(cru));
  } catch (error) {
    const detalhe = error && error.message ? error.message : '';
    throw new Error(detalhe
      ? `Não foi possível ler a compactação deste KMZ. ${detalhe}`
      : 'Não foi possível ler a compactação deste KMZ.');
  }
}

export async function extrairKml(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const textoDireto = new TextDecoder().decode(bytes.subarray(0, Math.min(bytes.length, 300)));
  // Zip armazenado sem compressão traz o <kml> à vista nos primeiros bytes; a assinatura PK
  // distingue ele de um KML puro.
  const ehZip = bytes[0] === 0x50 && bytes[1] === 0x4b;
  if (!ehZip && (textoDireto.includes('<kml') || textoDireto.includes('<coordinates'))) {
    return new TextDecoder().decode(bytes);
  }
  const entradas = entradasDoZip(buffer);
  const peloCentral = entradas.find((entry) => entry.name.toLowerCase().endsWith('.kml'));
  if (peloCentral) {
    const cru = fatiaLocal(bytes, view, peloCentral.localOffset, peloCentral.compSize);
    return textoDoKml(peloCentral.method, cru);
  }
  let offset = 0;
  while (offset + 30 <= bytes.length && view.getUint32(offset, true) === 0x04034b50) {
    const flags = view.getUint16(offset + 6, true);
    const method = view.getUint16(offset + 8, true);
    const compSize = view.getUint32(offset + 18, true);
    const nameLen = view.getUint16(offset + 26, true);
    const extraLen = view.getUint16(offset + 28, true);
    const nameStart = offset + 30;
    const name = new TextDecoder().decode(bytes.subarray(nameStart, nameStart + nameLen));
    const dataStart = nameStart + nameLen + extraLen;
    const varre = (flags & 0x8) !== 0 || compSize === 0;
    const dataEnd = varre ? proximaAssinatura(bytes, dataStart) : dataStart + compSize;
    if (name.toLowerCase().endsWith('.kml')) {
      const cru = bytes.subarray(dataStart, dataEnd);
      return textoDoKml(method, cru);
    }
    offset = dataEnd;
  }
  throw new Error('Não encontrei o KML dentro do KMZ.');
}

function base64De(bytes) {
  const passo = 0x8000;
  let binario = '';
  for (let i = 0; i < bytes.length; i += passo) {
    binario += String.fromCharCode.apply(null, bytes.subarray(i, i + passo));
  }
  return btoa(binario);
}

function mimeDoNome(nome) {
  const extensao = String(nome).split('.').pop().toLowerCase();
  return MIME_IMAGEM[extensao] || 'image/png';
}

// Lê do zip as imagens que o KML referencia. Devolve { chave: 'data:image/png;base64,…' }; o que
// não for achado fica de fora e o ponto segue no mapa, só sem o símbolo.
export async function extrairImagens(buffer, pedidos) {
  const chaves = Object.keys(pedidos || {});
  const saida = {};
  if (!chaves.length) return saida;
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const porNome = new Map();
  entradasDoZip(buffer).forEach((entrada) => {
    porNome.set(entrada.name, entrada);
    porNome.set(entrada.name.toLowerCase(), entrada);
  });
  await Promise.all(chaves.map(async (chave) => {
    const caminho = pedidos[chave];
    const candidatos = [caminho];
    try {
      const decodificado = decodeURIComponent(caminho);
      if (decodificado !== caminho) candidatos.push(decodificado);
    } catch (error) {
      // nome com % solto: fica só o caminho literal
    }
    const entrada = candidatos
      .map((candidato) => porNome.get(candidato) || porNome.get(candidato.toLowerCase()))
      .find(Boolean);
    if (!entrada) return;
    try {
      const cru = fatiaLocal(bytes, view, entrada.localOffset, entrada.compSize);
      let dados = null;
      if (entrada.method === 0) dados = cru;
      else if (entrada.method === 8) dados = await inflar(cru);
      if (dados) saida[chave] = `data:${mimeDoNome(entrada.name)};base64,${base64De(dados)}`;
    } catch (error) {
      // imagem ilegível: o ponto segue sem o símbolo
    }
  }));
  return saida;
}

// Blob.arrayBuffer não existe em navegador antigo, e um Blob que passou por JSON (Redux
// persistido) vira objeto simples. Em vez de "arrayBuffer is not a function", diz o que houve.
async function bytesDoArquivo(file) {
  if (file && typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  if (typeof Blob !== 'undefined' && file instanceof Blob && typeof FileReader !== 'undefined') {
    return new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(leitor.result);
      leitor.onerror = () => reject(leitor.error);
      leitor.readAsArrayBuffer(file);
    });
  }
  throw new Error('O arquivo KMZ não está mais na memória do navegador. Recarregue a NSIR e tente de novo.');
}

export async function geometriaDoArquivo(file, opcoes) {
  const nome = file && file.name ? file.name.toLowerCase() : '';
  const buffer = await bytesDoArquivo(file);
  const ehKml = nome.endsWith('.kml');
  const xml = ehKml
    ? new TextDecoder().decode(buffer)
    : await extrairKml(buffer);
  const { iconesRelativos, ...geo } = geometriaDoKml(xml, opcoes);
  return { ...geo, icones: ehKml ? {} : await extrairImagens(buffer, iconesRelativos) };
}
